/* ════════════════════════════════════════════════════════════════════════
   H1 — Home at Easy M, the basic monitoring composition (wireframe 01).

   The screen every Easy M customer lands on. Built from the drawing, in the
   drawing's own component classes (sk-components.css v0.2.0), so the type
   scale, chip shapes and table rules are the stylesheet's rather than this
   screen's: "a screen built from a wireframe needs no renaming."

   Composition rule, from the drawing: "Same components as H1 (E) and H1 (RO);
   the brief differs and the docket is absent. Nothing is a separate product."
   So this is a composition of Home, selected by TIER, not a second screen.

   Data budget, from the drawing: one view-model call for tiles and brief, one
   waterfall call, one 5-minute series pair plus irradiance, one server-ranked
   page of sites. Four calls, and no per-row fetching.
   ════════════════════════════════════════════════════════════════════════ */
import type { EnvelopeHomeData, Figure } from "@solarkey/api-client";
import { useGetV1Interventions } from "@solarkey/api-client";
import { Link } from "@tanstack/react-router";
import { EmptyState, Metric, StateWord, Term, atDisplayScale, basisLabel, briefText,
         figuresAtOneScale, formatAge, reasonText } from "@solarkey/ui";
import { useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { DateNav } from "../shell/DateNav";
import { TileTrend, type TileKey } from "../components/TileTrend";

/** Annotation 3: "every name printed". The wire carries the enum; these are the
 *  words the drawing prints, so a variant is named even when its figure is not. */
const PR_VARIANT_LABEL: Record<string, string> = {
  ABSOLUTE: "Absolute",
  GRID: "Grid",
  WEATHER: "Weather",
  EQUIPMENT: "Equipment",
  GRID_WEATHER_EQUIPMENT: "Grid+Weather+Equipment",
};
/** The energy tile is a PERIOD TOTAL, so its label must follow the period — on
 *  7D the figure is the 7-day total, not "today" (QA H1). The caret still opens
 *  the same trend. */
const ENERGY_LABEL: Record<string, string> = {
  today: "Energy today",
  "7d": "Energy · 7D",
  "30d": "Energy · 30D",
  custom: "Energy · range",
};
/** The server's raw sort token ("deviation:asc") read as words — never the token
 *  itself on the glass (UX-REV-001 U2). */
function sortPhrase(sort?: string): string {
  if (!sort) return "";
  const field = sort.split(":")[0] ?? "";
  const label: Record<string, string> = {
    deviation: "ranked by deviation from the fleet",
    pr: "ranked by PR", energy: "ranked by energy", power: "ranked by power",
    capacity: "ranked by capacity", stopped_capacity: "ranked by stopped capacity",
    coverage: "ranked by coverage", site: "by site name",
  };
  return ` · ${label[field] ?? "ranked"}`;
}

/** §6.1: each availability is named. `technical` is the plant figure. */
const AVAIL_LABEL: Record<string, string> = {
  technical: "Plant",
  grid: "Grid",
  energy_by_cause: "Energy by cause",
};

/** One open detection as /v1/interventions serves it. */
type InterventionItem = {
  id?: string; site_id?: string | null; title?: string | null; what?: string | null;
  severity?: string | null; class?: string | null; opened_at?: string | null;
};

/** Worst first. A critical buried under four warnings is a list that has given up
 *  the one job it has. */
const SEVERITY_RANK = (s?: string | null): number =>
  s === "critical" ? 3 : s === "warning" ? 2 : s === "info" ? 1 : 0;

type FleetRow = {
  site_id?: string;
  /** The registry's own name for the site ("Borgaon"), so a column people read
   *  down is not a list of lowercase slugs. Null where the registry has none. */
  site_name?: string | null;
  health?: { state?: string }; capacity_dc?: Figure; power?: Figure;
  energy_today?: Figure; pr?: Figure; pr_deviation?: Figure; stopped_capacity?: Figure;
  specific_yield?: Figure; cuf?: Figure;
  /** Cumulative plane-of-array insolation (kWh/m²) over the window — what the PR
   *  denominator is built from. Distinct from `irradiance`, the instantaneous
   *  W/m², which reads 0 after dark and so is useless in a daily grid. */
  insolation?: Figure;
  irradiance?: Figure; module_temperature?: Figure;
  coverage?: Figure; open_interventions?: number;
  freshness?: { age_s?: number; state?: string; last_at?: string | null };
  rank_tier?: string;
};
type FleetBlock = {
  site_count?: number; returned?: number; sort?: string;
  capacity_dc_total?: Figure; pr_reference?: Figure;
};

/** A figure the drawing shows as a big number, or its state word. Never a dash.
 *
 *  `scaled` says the caller has already put this figure on a shared scale with
 *  its neighbours, so it must not be rescaled on its own. It also fixes the
 *  decimals: in a column, 4 and 4.00 do not line up, and three places keep a
 *  small band legible once the column is in the larger unit (17 kWh is 0.017
 *  MWh — at two places it would print as 0.02, and a genuinely small row would
 *  round to a zero the ruling does not allow a figure to show). */
function Val({ figure, scaled }: { figure?: Figure; scaled?: boolean }): ReactNode {
  if (!figure) return <StateWord state="CANNOT_CHECK" reason={{ template: "CANNOT_CHECK_FIGURE_ABSENT" }} />;
  if (typeof figure.value !== "number") return <StateWord state={figure.state} reason={figure.reason} />;
  // Shown at the scale the drawings use: a fleet in MW, not six digits of kW.
  // Freshness is NOT suppressed: only a figure the server stamped as live-ish
  // carries the object, so a period total renders no mark while the live power
  // tile reads "Live" or names its instant.
  return <Metric figure={scaled ? figure : (atDisplayScale(figure) ?? figure)}
                 showBasis={false} showProvenance={false}
                 {...(scaled ? { maximumFractionDigits: 3, minimumFractionDigits: 3 } : {})} />;
}

/** The drawing's `Provisional` chip: a figure that covers fewer sites than it names. */
function CoverageChip({ coverage }: { coverage?: EnvelopeHomeData["tiles"] }): ReactNode {
  const c = coverage as unknown as { provisional?: boolean; sites_counted?: number; sites_in_scope?: number } | undefined;
  if (!c?.provisional) return null;
  const reason = { template: "PROVISIONAL_SITE_COVERAGE",
                   params: { sites: c.sites_counted, sites_total: c.sites_in_scope } };
  return <span className="chip state" title={reasonText(reason) ?? undefined}>Provisional</span>;
}

export function HomeEasyM({ h, scopeLabel, from, to, sites, fleet, period, mode, tenant,
                            onPeriod, onWindow, window: win }: {
  h: EnvelopeHomeData;
  scopeLabel: string;
  from?: string;
  to?: string;
  sites: FleetRow[];
  fleet?: FleetBlock;
  period?: string;
  mode?: string;
  /** Portfolio scope === tenant. A site ROW opens that site; the tiles do not
   *  navigate at all — their caret opens the figure's history in place. */
  tenant?: string;
  onPeriod?: (p: string) => void;
  onWindow?: (from: string, to: string) => void;
  /** The window the SERVER resolved. `start`/`end` are inclusive local DATES,
   *  which the date navigator shows; `start_at`/`end_at` are the half-open
   *  instants a telemetry read takes. The chart must use the instants — the
   *  dates make a zero-width window on the live day (start === end). */
  window?: { start?: string; end?: string; start_at?: string; end_at?: string };
}): ReactNode {
  const t = h.tiles;
  const dp = h.data_path;
  /* The caret is a DISCLOSURE, not a link (UX-007 §9): it opens the tile's own
     history under the row and leaves the tiles on screen. One open at a time.
     The tile body itself is not a control — a figure whose card navigates away
     from the screen that states it was the wrong reading of the drawing. */
  const [openTile, setOpenTile] = useState<TileKey | null>(null);
  const caret = (key: TileKey): ReactNode => {
    const on = openTile === key;
    return (
      <button type="button" className={on ? "caret on" : "caret"}
              aria-expanded={on}
              aria-label={on ? "Hide the trend" : "Show the trend"}
              onClick={() => setOpenTile(on ? null : key)}>
        &#9662;
      </button>
    );
  };

  /* PR and Availability carry their variants' figures on the wire, so switching a
     chip is a client-side selection — the big number follows the chip, no refetch.
     Null means "use the server's selected variant" until the viewer picks one. */
  const [prBasis, setPrBasis] = useState<string | null>(null);
  const [availBasis, setAvailBasis] = useState<string | null>(null);

  const prVariants = (t?.pr?.variants ?? []) as unknown as { variant?: string; figure?: Figure }[];
  const prSelected = prBasis ?? String(t?.pr?.selected ?? "");
  const prShown = prVariants.find((v) => String(v.variant) === prSelected)?.figure ?? t?.pr?.figure;

  const availVariants = (t?.availability?.variants ?? []) as unknown as { name?: string; figure?: Figure }[];
  const availSelected = availBasis ?? String(t?.availability?.selected ?? "");
  const availShown = availVariants.find((v) => String(v.name) === availSelected)?.figure ?? t?.availability?.figure;

  return (
    // `.content` adds its own 20px side gutter, but the shell's RootLayout already
    // gives the main area a 20px gutter — so this screen sat one gutter (20px)
    // further from the rail than screens like Alerts, which render their PageHeader
    // straight into RootLayout. Zero the horizontal gutter here so Home lines up
    // with the rail exactly like every other screen; RootLayout's gutter stands.
    // Vertical padding is kept. (QA.)
    <div className="content" style={{ paddingLeft: 0, paddingRight: 0 }}>
      {/* ── The page head the drawing carries: title, the scope line with the
             fleet's own counts, the time control and the date navigator. ── */}
      {/* `.pagehead` carries its own 20px side padding too, so it is zeroed here as
          well — the header row then aligns with the cards below it and with the
          rail. */}
      <div className="pagehead" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <h3>Home</h3>
        <span className="meta">
          {scopeLabel}
          {fleet ? <> · {fleet.site_count} {fleet.site_count === 1 ? "site" : "sites"}</> : null}
          {typeof fleet?.capacity_dc_total?.value === "number"
            ? <> · <Metric figure={atDisplayScale(fleet.capacity_dc_total) ?? fleet.capacity_dc_total}
                           showBasis={false} showProvenance={false} showFreshness={false} /></>
            : null}
        </span>
        <div className="timectl">
          {["today", "7d", "30d"].map((k) => (
            <span key={k} className={(period ?? "today") === k ? "on" : ""}
                  role="button" tabIndex={0}
                  onClick={() => onPeriod?.(k)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPeriod?.(k); } }}>
              {k === "today" ? "Today" : k.toUpperCase()}
            </span>
          ))}
        </div>
        {/* The date navigator and the live chip (drawing: "‹ 06 Oct 2026 ›  Live").
            DateNav is the shell's own control: it steps the window, refuses to
            step past the last completed day, and writes from/to into the URL. */}
        <DateNav
          period={(period ?? "today") as never}
          from={from}
          to={to}
          mode={mode as never}
          resolved={{ from: win?.start, to: win?.end }}
          onNavigate={(f, tt) => onWindow?.(f, tt)}
        />
        {/* Only the live DAY is live. The chip keyed off `!from`, so picking 7D or
            30D — which sets a period but no explicit from/to — left a lit "Live"
            beside a month of completed days. A range is named, not called live. */}
        {(period ?? "today") === "today" ? (
          <span className={mode === "live" || !from ? "chip on" : "chip"}
                title="Home is always live — there is no replay or forecast mode here.">Live</span>
        ) : (
          <span className="chip" title="A completed range. Live applies to today only.">
            {(period ?? "").toUpperCase()}
          </span>
        )}
      </div>
      {/* ── The six tiles. The PR tile was the widest (1.7fr) back when it printed
             all five variant names; now that it carries one figure + a compact
             basis switcher, that width left a large empty band, so it is trimmed
             to 1.2fr. Even the longest label ("PR · Grid+Weather+Equipment", ~147px)
             still fits on one line at this width. ── */}
      <div className="row" style={{ gridTemplateColumns: "1fr 1.1fr 1.2fr 1fr 1fr 1fr" }}>
        <div className={openTile === "active_power" ? "tile open" : "tile"}>
          {caret("active_power")}
          <div className="lab">Active power</div>
          <div className="val"><Val figure={t?.active_power?.figure} /></div>
          <div className="sub">
            {typeof t?.active_power?.pct_of_ac?.value === "number"
              ? <>{t.active_power.pct_of_ac.value.toFixed(0)}% of AC</>
              : <StateWord state={t?.active_power?.pct_of_ac?.state ?? "CANNOT_CHECK"}
                           reason={t?.active_power?.pct_of_ac?.reason} />}
            {" "}<CoverageChip coverage={t?.active_power?.coverage as never} />
          </div>
        </div>

        <div className={openTile === "energy_today" ? "tile open" : "tile"}>
          {caret("energy_today")}
          <div className="lab">{ENERGY_LABEL[(period ?? "today").toLowerCase()] ?? "Energy"}</div>
          <div className="val"><Val figure={t?.energy_today?.figure} /></div>
          <div className="sub">
            {/* Annotation 2: the basis chips, and the same figure the fleet grid
                and the site page serve — one canonical figure per quantity. */}
            {basisChipsWorthShowing(t?.energy_today?.basis_chips).map((chip, i) => {
              const c = chip as unknown as { basis?: string; share?: Figure };
              const share = c.share;
              return (
                <span key={i} className="chip basis"
                      title={typeof share?.value !== "number"
                             ? reasonText(share?.reason) ?? undefined : undefined}>
                  {/* UX-REV-001 U3: the readable basis label ("Inverter-derived"),
                      not the long all-caps token that truncated to "INVERTER-DERIVED
                      Cann…". The chip wraps rather than clipping (CSS). */}
                  {basisLabel(c.basis) ?? String(c.basis ?? "")}{" "}
                  {typeof share?.value === "number"
                    ? `${share.value.toFixed(0)}%`
                    : <StateWord state={share?.state ?? "CANNOT_CHECK"} reason={share?.reason} />}
                </span>
              );
            })}
            <CoverageChip coverage={t?.energy_today?.coverage as never} />
          </div>
        </div>

        <div className={openTile === "pr" ? "tile open" : "tile"}>
          {caret("pr")}
          <div className="lab"><Term tkey="pr">PR</Term> · {PR_VARIANT_LABEL[prSelected] ?? prSelected.replace(/_/g, "+")}</div>
          <div className="val"><Val figure={prShown} /></div>
          <WithheldWhy figure={prShown} />
          <div className="sub">
            {/* UX-REV-001 U4: a summary card carries ONE figure + a way to switch,
                not the whole method menu. The five-variant comparison lives on
                Performance; here a compact basis switcher changes the figure above
                (client-side — every variant is already on the wire).

                A variant with no figure stays SELECTABLE. It used to be a disabled
                option, which meant the one question a reader actually has about
                Equipment PR — "why is there no number?" — could only be answered by
                hovering a greyed-out entry they could not choose. Now picking it
                shows the withheld state word in the tile and prints the reason
                underneath, which is the platform's own grammar for an absent
                figure. Withholding a number is not the same as hiding it. */}
            <label className="basis-switch">
              <span className="muted">basis</span>
              {/* UX-REV follow-up: the switch read too big because a native <select>
                  reserves width for its LONGEST option ("Grid+Weather+Equipment"),
                  leaving "Absolute" with a wide empty gap. field-sizing:content sizes
                  it to the current value — a tight chip that grows only when a longer
                  variant is picked. Where unsupported it falls back to the CSS box. */}
              <select aria-label="PR basis"
                      value={prSelected}
                      onChange={(e) => setPrBasis(e.target.value)}
                      style={{ fieldSizing: "content" } as React.CSSProperties}>
                {(t?.pr?.variants ?? []).map((v) => {
                  const item = v as unknown as { variant?: string; figure?: Figure };
                  const key = String(item.variant ?? "");
                  const held = typeof item.figure?.value !== "number";
                  return (
                    <option key={key} value={key}>
                      {PR_VARIANT_LABEL[key] ?? key}{held ? " — withheld" : ""}
                    </option>
                  );
                })}
              </select>
            </label>
            <CoverageChip coverage={t?.pr?.coverage as never} />
          </div>
        </div>

        <div className={openTile === "availability" ? "tile open" : "tile"}>
          {caret("availability")}
          <div className="lab"><Term tkey="technical_availability">Availability</Term></div>
          <div className="val"><Val figure={availShown} /></div>
          <WithheldWhy figure={availShown} />
          <div className="sub">
            {/* §6.1: each availability is named. There is no bare "availability".
                The chips switch the figure above. */}
            {(t?.availability?.variants ?? []).map((v) => {
              const item = v as unknown as { name?: string; figure?: Figure };
              const key = String(item.name ?? "");
              const on = key === availSelected;
              const held = typeof item.figure?.value !== "number";
              // A withheld variant (Energy by cause, until the Availability engine
              // sends one) is still SELECTABLE. It used to be inert, so the question
              // it raises — why is there no number? — had no way to be answered
              // except by hovering a chip that refused to act. Picking it now puts
              // the withheld state word in the tile and the reason under it.
              return (
                <button key={key} type="button" className={on ? "chip on" : "chip"}
                        aria-pressed={on}
                        title={held ? reasonText(item.figure?.reason) ?? undefined : undefined}
                        style={held ? { opacity: 0.7 } : undefined}
                        onClick={() => setAvailBasis(key)}>
                  {AVAIL_LABEL[key] ?? key}{held ? " ·" : ""}
                </button>
              );
            })}
            <CoverageChip coverage={t?.availability?.coverage as never} />
          </div>
        </div>

        <div className={openTile === "sites_reporting" ? "tile open" : "tile"}>
          {caret("sites_reporting")}
          <div className="lab">Sites reporting</div>
          <div className="val">
            {/* The drawing prints the PAIR: reporting of monitored. The bare count
                alone does not say what it is out of (annotation 4). */}
            {typeof t?.sites_reporting?.reporting === "number"
             && typeof t?.sites_reporting?.monitored === "number"
              ? <>{t.sites_reporting.reporting} / {t.sites_reporting.monitored}</>
              : <Val figure={t?.sites_reporting?.figure} />}
          </div>
          <div className="sub">
            {/* Annotation 4: paths down are EVENTS, not red sites. */}
            {(t?.sites_reporting?.paths_down ?? []).length > 0 ? (
              <span className="st off"><i />{(t?.sites_reporting?.paths_down ?? []).length} paths down</span>
            ) : (
              <span className="st ok"><i />all paths up</span>
            )}
          </div>
        </div>

        <div className={openTile === "alerts_open" ? "tile open" : "tile"}
             title="Open DEVICE alerts across all equipment. Distinct from platform FINDINGS (shown per site in Alerts by site and the site table).">
          {caret("alerts_open")}
          <div className="lab">Device alerts open</div>
          <div className="val"><Val figure={t?.alerts_open?.figure} /></div>
          <div className="sub">
            {typeof t?.alerts_open?.critical?.value === "number"
             && typeof t?.alerts_open?.warning?.value === "number"
              ? <>{t.alerts_open.critical.value} critical, {t.alerts_open.warning.value} warning</>
              : <StateWord state="CANNOT_CHECK" reason={t?.alerts_open?.figure?.reason} />}
          </div>
        </div>
      </div>

      {/* The open tile's own history, under the row it belongs to. The window
          decides what it is: one day draws telemetry on a clock axis, a range
          draws the engine's daily figures on a date axis. */}
      {openTile ? (
        <TileTrend tile={openTile} win={win} onClose={() => setOpenTile(null)}
                   sites={sites.map((r) => String(r.site_id ?? "")).filter(Boolean)} />
      ) : null}

      {/* ── The brief, in words (annotation 5), composed from the server's own
             clauses. No docket: nothing at this tier needs a decision. ── */}
      <div className="card">
        <h4>
          What changed{h.brief?.since ? ` since ${String(h.brief.since)}` : ""}
          <span className="sub">the brief, in words</span>
        </h4>
        <div style={{ fontSize: 12, lineHeight: 1.55 }}>
          {(h.brief?.parts ?? [])
            .map((part) => briefText(part as never))
            .filter((s): s is string => Boolean(s))
            .join(" ")}
        </div>
        <div className="row"
             style={{ gridTemplateColumns: "auto repeat(5,max-content)", gap: 18, fontSize: 10.5,
                      marginTop: 8, alignItems: "center", justifyContent: "start" }}>
          <span className="muted">Data path, per hop</span>
          {(dp?.hops ?? []).map((hop, i) => {
            const hp = hop as unknown as {
              source_kind?: string;
              freshness?: { state?: string; age_s?: number };
            };
            const state = hp.freshness?.state;
            const cls = state === "live" ? "st ok" : state === "stale" ? "st warn" : "st off";
            return (
              <span key={i} className={cls}>
                <i />{hp.source_kind}
                {typeof hp.freshness?.age_s === "number" ? ` ${formatAge(hp.freshness.age_s)}` : ""}
              </span>
            );
          })}
          {(dp?.hops_withheld ?? []).map((hop, i) => {
            const hw = hop as unknown as { source_kind?: string; reason?: { template?: string } };
            return (
              <span key={`w${i}`} className="st" title={reasonText(hw.reason as never) ?? undefined}>
                <i />{hw.source_kind}{" "}
                <StateWord state="NOT_MEASURED" reason={hw.reason as never} />
              </span>
            );
          })}
        </div>
      </div>

      {/* ── Waterfall · alerts by site ──────────────────────────────────────
          The expected-vs-delivered chart sat first in this row and is deferred:
          it was drawn from a reference power model that is no longer part of this
          platform. Two columns now, in the drawing's remaining proportions. */}
      <div className="row" style={{ gridTemplateColumns: "1.2fr .9fr" }}>
        <WaterfallCard h={h} window={win} />
        <AlertsBySite h={h} sites={sites} scopeLabel={scopeLabel}
                      tenant={tenant} period={period} />
      </div>

      <SiteTable sites={sites} fleet={fleet} tenant={tenant} period={period} />
    </div>
  );
}

/** The window the SERVER resolved, as the card's subtitle.
 *
 *  The subtitle used to read "live day" always. It is the ladder that is drawn
 *  here, and the ladder closes a COMPLETED day — so on any period but today the
 *  card was labelling someone else's day as the live one. Naming the window the
 *  server actually resolved is the difference between a reader trusting the
 *  figures and checking them. */
export function windowLabel(win?: { start?: string; end?: string }): string {
  const start = win?.start;
  const end = win?.end;
  if (!start && !end) return "live day";
  if (start && end && start === end) return start;
  return [start, end].filter(Boolean).join(" – ");
}

/* ── "Where the energy went" (annotation 10). The live-day cascade is not
      served, and UX-006 §5.6 forbids drawing a waterfall with no potential, so
      the card states that rather than drawing thirteen empty rows. ── */
function WaterfallCard({ h, window: win }: {
  h: EnvelopeHomeData;
  window?: { start?: string; end?: string };
}): ReactNode {
  const rows = (h.losses ?? []) as unknown as { label?: string; figure?: Figure; row_status?: string }[];
  const potential = rows.find((r) => r.label === "potential");
  const drawable = typeof potential?.figure?.value === "number";
  // ONE unit down the whole column. Scaling row by row gave potential in MWh and
  // downtime in kWh in the same table, which hides the thing a cascade exists to
  // show — how big each step is against the one above it.
  const scaled = figuresAtOneScale(rows.map((r) => r.figure)).figures;
  return (
    <div className="card">
      <h4>Where the energy went <span className="sub">{windowLabel(win)}</span></h4>
      {drawable ? (
        <table className="wf compact">
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.label}</td>
                <td className="num"><Val figure={scaled[i]} scaled /></td>
                <td className="muted">{r.row_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        /* UX-REV-001 U1: a quiet one-line empty state, not a paragraph citing an
           internal chart-spec clause. The reason rides as a tooltip. */
        <EmptyState variant="withheld" title="No energy breakdown yet"
                    reason={potential?.figure?.reason
                            ?? { template: "NOT_MEASURED_NO_POTENTIAL_ROW" }} />
      )}
    </div>
  );
}

/* ── Alerts by site (annotation 7): "device alerts ranked by the capacity
      affected, stopped or not reporting, each with its state word. Executor is
      none at this tier; a person acts from here." The ranking is the server's —
      the grid already orders by deviation then stopped capacity — so this reads
      the same rows rather than sorting a second time in the browser. ── */
function AlertsBySite({ h, sites, scopeLabel, tenant, period }: {
  h: EnvelopeHomeData; sites: FleetRow[]; scopeLabel: string;
  tenant?: string; period?: string;
}): ReactNode {
  // The card named sites a reader wanted to open and then did nothing when they
  // clicked one — the one thing a list of problems exists to let you do.
  const navigate = useNavigate();
  const openSite = (slug?: string | null): void => {
    if (!tenant || !slug) return;
    void navigate({
      to: "/t/$tenant/s/$scope/plants/$site/now",
      params: { tenant, scope: tenant, site: slug },
      search: { period: period ?? "today", mode: "live" },
    } as never);
  };
  // The REAL open detections, fleet-wide — the same rule engine that drives
  // /v1/interventions and the Findings count, so the card and the alerts page
  // cannot disagree. The card used to list sites by stopped capacity and a bare
  // finding COUNT, which told a reader something was wrong but never what.
  const detections = useGetV1Interventions();
  const open = ((detections.data?.data?.data?.items ?? []) as InterventionItem[])
    // A detection that cannot say which site it belongs to is not something a
    // person can act on. 200 engine VERDICTS arrive on this endpoint with no
    // site, title or severity and would otherwise bury the real ones.
    .filter((d) => d.site_id && d.title)
    .sort((a, b) => SEVERITY_RANK(b.severity) - SEVERITY_RANK(a.severity));
  const nameOf = (slug?: string | null): string =>
    siteLabel(sites.find((s) => s.site_id === slug) ?? { site_id: slug });

  return (
    <div className="card">
      <h4 title="Open rule-based detections across the fleet — the same ones the Alerts page lists.">
        Attention by site <span className="sub">open now · fleet-wide</span>
        {tenant ? (
          <Link to="/t/$tenant/s/$scope/alerts"
                params={{ tenant, scope: tenant }}
                style={{ float: "right", fontSize: 11, fontWeight: 400 }}>
            All alerts &rarr;
          </Link>
        ) : null}
      </h4>
      {detections.isPending ? (
        <div style={{ fontSize: 12 }} className="muted">Loading…</div>
      ) : detections.isError ? (
        // Never an empty "all clear" when the read failed: that is the one wrong
        // answer, because it reassures.
        <StateWord state="CANNOT_CHECK"
                   reason={{ template: "CANNOT_CHECK_DETECTIONS_UNAVAILABLE" }} />
      ) : open.length === 0 ? (
        <div style={{ fontSize: 12 }} className="muted">
          {h.data_path?.verified
            ? `Nothing open in ${scopeLabel || "this scope"}.`
            : <StateWord state="CANNOT_CHECK"
                         reason={{ template: "CANNOT_CHECK_QUIET_DATA_PATH_UNVERIFIED" }} />}
        </div>
      ) : (
        <table className="table compact">
          <tbody>
            {open.map((d) => (
              <tr key={d.id}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: tenant && d.site_id ? "pointer" : undefined }}
                  onClick={() => openSite(d.site_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openSite(d.site_id); }
                  }}>
                <td>
                  <b>{nameOf(d.site_id)}</b>{" "}
                  <span className={d.severity === "critical" ? "st off" : "st warn"}>
                    <i />{d.severity ?? "warning"}
                  </span>
                  {/* The drawing's point: name the fault and the evidence for it,
                      not a count. "Low performance ratio — PR 60.4%". */}
                  <div className="muted" style={{ fontSize: 10 }}>
                    {d.title}{d.what ? ` — ${d.what}` : ""}
                  </div>
                </td>
                <td className="num" style={{ whiteSpace: "nowrap" }}>
                  {d.opened_at ? updatedAt({ last_at: d.opened_at }) : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Why a selected figure has no number, in words, under the tile.
 *
 *  A withheld figure already states WHAT it is — "Cannot check" — through its state
 *  word. What it never said was WHY, because the reason rode as a `title` tooltip,
 *  which on a headline tile is the same as not saying it: nobody hovers a number to
 *  find out why it is missing, and on a touch screen they cannot.
 *
 *  Renders nothing for a figure that has a value, so the tiles are unchanged in the
 *  normal case. */
function WithheldWhy({ figure }: { figure?: Figure }): ReactNode {
  if (!figure || typeof figure.value === "number") return null;
  const why = reasonText(figure.reason);
  if (!why) return null;
  return (
    <div className="muted" style={{ fontSize: 10, lineHeight: 1.4, marginTop: 2 }}>
      {why}
    </div>
  );
}

/** The basis chips worth putting on the tile.
 *
 *  The server sends every basis it knows about, withheld ones included, and it is
 *  right to: the wire should not hide that INVERTER_DERIVED exists and had no
 *  source. The TILE is a different question. Where the designated basis already
 *  accounts for the whole figure — "Meter 100%" — a second chip reading
 *  "Inverter-derived · Cannot check" answers a question nobody asked and reads as
 *  a fault on a figure that is complete.
 *
 *  So: a chip that carries a number is always shown, and the withheld ones appear
 *  only when NO chip carries a number — which is exactly when the reader does need
 *  to know which bases were tried and why each came back empty.
 */
function basisChipsWorthShowing<T>(chips: readonly T[] | undefined): readonly T[] {
  const all = chips ?? [];
  const measured = all.filter(
    (c) => typeof (c as { share?: Figure }).share?.value === "number",
  );
  return measured.length > 0 ? measured : all;
}

/** A site as a person names it: the registry's own name, else the identifier made
 *  readable. The grid printed the raw slug, so a column people read down was
 *  "borgaon / padoli / pimpalgaon" in machine lowercase. Never invents a name —
 *  with no registry name it title-cases the identifier it already had. */
function siteLabel(s: { site_name?: string | null; site_id?: string | null }): string {
  const name = (s.site_name ?? "").trim();
  if (name) return name;
  return (s.site_id ?? "")
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Units as they are written, not as ASCII survived them: m² and °C carry real
 *  characters, and `degC` is a wire token, not something to print at a reader. */
const UNIT_TEXT: Record<string, string> = {
  degC: "°C",
  "W/m2": "W/m²",
  "kWh/m2": "kWh/m²",
  "kWh/kWp": "kWh/kWp",
};
function prettyUnit(unit: string): string {
  return UNIT_TEXT[unit] ?? unit.replace(/m2\b/g, "m²");
}

/** Whether the PLANT is generating — which is what a control room means by status.
 *
 *  This column has now been wrong twice in the same place, each time by answering a
 *  question next to the one being asked. It began as `health` (the equipment's
 *  condition: a healthy plant can be silent for two days). It then became the data
 *  path's own state, and read "Live" for three plants at 20:20 with the sun down,
 *  because the TELEMETRY was live. Nobody reads a status column and asks about the
 *  telemetry; they ask whether the plant is running. "Live" beside a plant producing
 *  0 kW is simply false in the only sense a reader takes it.
 *
 *  Three states, and the third is the one that keeps it honest:
 *    Active   — reporting, and producing.
 *    Offline  — reporting, and not producing (after dark, or a real outage; the
 *               power and energy columns beside it say which).
 *    No data  — not reporting. We cannot see the plant, so we do not claim either
 *               way; the old platform called this "Offline" too, which quietly
 *               asserts a plant is down when the truth is that nobody is looking.
 */
export function SiteStatus({ row }: { row: FleetRow }): ReactNode {
  const path = row.freshness?.state;
  if (path === "path_down" || path === "stale" || !path) {
    return (
      <span className="st off"
            title={row.freshness?.last_at
              ? `No current telemetry — last reading ${row.freshness.last_at}. Whether the plant is generating cannot be established.`
              : "No telemetry is arriving, so whether the plant is generating cannot be established."}>
        <i />No data
      </span>
    );
  }
  const kw = row.power?.value;
  if (typeof kw !== "number") {
    // Reporting, but the power figure itself is withheld — say so in its own words
    // rather than guessing a status from an absence.
    return <StateWord state={row.power?.state ?? "CANNOT_CHECK"} reason={row.power?.reason} />;
  }
  // Any real export counts, the way the reference platform treats one live inverter
  // on a fourteen-inverter plant as Active. At night this goes to zero or negative
  // (the plant draws auxiliary power), which is not generation.
  return kw > 0
    ? <span className="st ok" title="Reporting and generating."><i />Active</span>
    : <span className="st neu" title="Reporting, but not generating — after dark, or an outage. The power and energy columns say which."><i />Offline</span>;
}

/** The instant a row is for, as a clock time — with the date when it is not today,
 *  so a reading from Friday cannot read as one from this afternoon. */
function updatedAt(f?: { last_at?: string | null; age_s?: number | null } | null): ReactNode {
  const iso = f?.last_at;
  if (!iso) return typeof f?.age_s === "number" ? formatAge(f.age_s) : "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (sameDay) return time;
  const day = d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return `${day} ${time}`;
}

/* ── The site table (annotation 9): "the columns a control room reads, each with
      its unit in the header. Ranked by deviation, never alphabetical." The order
      is the server's; the browser does not re-sort it. ── */
function SiteTable({ sites, fleet, tenant, period }: {
  sites: FleetRow[]; fleet?: FleetBlock; tenant?: string; period?: string;
}): ReactNode {
  const navigate = useNavigate();
  const openSite = (slug?: string) => {
    if (!tenant || !slug) return;
    void navigate({
      to: "/t/$tenant/s/$scope/plants/$site/now",
      params: { tenant, scope: tenant, site: slug },
      search: { period: period ?? "today", mode: "live" },
    } as never);
  };
  // Annotation 9 / UX-005 §4.9: "each with its unit in the header." Every scaled
  // column reads in ONE unit (taken from the figures via figuresAtOneScale, never a
  // hardcoded literal), stated once in the header, and the cells print bare — so the
  // grid is not "8.4 MWp · 3.24 MW · 28.43 MWh" on every row.
  const cap = figuresAtOneScale(sites.map((s) => s.capacity_dc));
  const pow = figuresAtOneScale(sites.map((s) => s.power));
  const en = figuresAtOneScale(sites.map((s) => s.energy_today));
  const sy = figuresAtOneScale(sites.map((s) => s.specific_yield));
  const pr = figuresAtOneScale(sites.map((s) => s.pr));
  const dev = figuresAtOneScale(sites.map((s) => s.pr_deviation));
  const cuf = figuresAtOneScale(sites.map((s) => s.cuf));
  // INSOLATION, not irradiance: the column answers "how much sun did this site get
  // over the window", which is the cumulative kWh/m² the PR denominator is built
  // from. It printed the INSTANTANEOUS W/m² instead, so every row read 0 after dark
  // — a column of zeros next to a day's real energy.
  const ins = figuresAtOneScale(sites.map((s) => s.insolation));
  const mod = figuresAtOneScale(sites.map((s) => s.module_temperature));
  const cov = figuresAtOneScale(sites.map((s) => s.coverage));

  // The unit sits in brackets beside the label and keeps its own casing (the header's
  // uppercase rule is scoped off `.unit` in CSS), so a column reads "PR (%)" and
  // "Insolation (kWh/m²)" — not "PR %" and "INSOLATION KWH/M2".
  const u = (unit: string): ReactNode =>
    unit ? <span className="unit"> ({prettyUnit(unit)})</span> : null;
  // A pre-scaled figure, value only — the column header carries the unit.
  //
  // `dp` is FIXED per column, minimum and maximum together. Without it the same
  // column printed 23.36, 29.89 and 22.6, or 92.31 and 100 — the decimal points
  // wandered row to row and the column stopped reading as a column. A reader
  // comparing three sites compares digit positions before they read the digits.
  const bare = (f: Figure | undefined, dp: number): ReactNode =>
    f && typeof f.value === "number"
      ? <Metric figure={f} showUnit={false} showBasis={false}
                showProvenance={false} showFreshness={false}
                minimumFractionDigits={dp} maximumFractionDigits={dp} />
      : <StateWord state={f?.state ?? "CANNOT_CHECK"} reason={f?.reason} />;
  return (
    <div className="card">
      {/* Thirteen columns do not fit a phone, and the page body must never scroll
          sideways — so the TABLE scrolls inside its own card instead. */}
      <div style={{ overflowX: "auto" }}>
      <table className="table compact fleetgrid">
        <thead>
          <tr>
            <th>Site</th>
            {/* Was "Health", which answered a different question from the one a
                control room asks first. Health is the equipment's condition; this
                says whether the site is talking to us at all. */}
            <th>Status</th>
            <th className="num">Capacity{u(cap.unit)}</th>
            <th className="num">Power{u(pow.unit)}</th>
            <th className="num">Energy today{u(en.unit)}</th>
            <th className="num">Specific yield{u(sy.unit)}</th>
            <th className="num">PR{u(pr.unit)}</th>
            {/* "vs fleet" alone did not say vs fleet WHAT. It is this site's PR minus
                the fleet's, so the difference of two percentages is in POINTS — the
                figure's own "%" unit would have read as a percentage of a percentage. */}
            <th className="num"
                title="This site's PR minus the fleet PR, in percentage points. Positive is above the fleet.">
              PR vs fleet<span className="unit"> (pts)</span>
            </th>
            <th className="num">CUF{u(cuf.unit)}</th>
            <th className="num" title="Plane-of-array insolation over the window — the cumulative sun this site received, which the PR denominator is built from.">
              Insolation{u(ins.unit)}
            </th>
            <th className="num">Module{u(mod.unit)}</th>
            <th className="num">Coverage{u(cov.unit)}</th>
            {/* "Seen · 1m" made the reader do arithmetic to find the thing they
                wanted: which reading this row is. Name the instant. */}
            <th className="num">Updated</th>
          </tr>
        </thead>
        <tbody>
          {sites.map((s, i) => (
            <tr key={s.site_id}
                role="button"
                tabIndex={0}
                style={{ cursor: tenant && s.site_id ? "pointer" : undefined }}
                onClick={() => openSite(s.site_id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openSite(s.site_id); }
                }}>
              {/* The site's own name, not its slug. A column of "borgaon",
                  "dawachi-wadi" read as machine text in a table people read down. */}
              <td><b>{siteLabel(s)}</b></td>
              <td><SiteStatus row={s} /></td>
              <td className="num">{bare(cap.figures[i], 1)}</td>
              <td className="num">{bare(pow.figures[i], 1)}</td>
              <td className="num">{bare(en.figures[i], 2)}</td>
              <td className="num">{bare(sy.figures[i], 2)}</td>
              <td className="num">{bare(pr.figures[i], 2)}</td>
              <td className="num">{bare(dev.figures[i], 2)}</td>
              <td className="num">{bare(cuf.figures[i], 2)}</td>
              <td className="num">{bare(ins.figures[i], 2)}</td>
              <td className="num">{bare(mod.figures[i], 1)}</td>
              <td className="num">{bare(cov.figures[i], 1)}</td>
              <td className="num">{updatedAt(s.freshness)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="muted" style={{ fontSize: 10.5, marginTop: 6 }}>
        {fleet ? <>Showing {fleet.returned ?? sites.length} of {fleet.site_count ?? sites.length} sites{sortPhrase(fleet.sort)}</> : null}
      </div>
    </div>
  );
}
